import { selectedProcessIdAtom } from '../../atoms/workspace';
import { useProcess } from '../../hooks/useProcess';
import { trpc } from '../../lib/trpc';
import { useDatabase } from '../../rxdb/provider';
import { InlineEditable } from '../InlineEditable';
import { ProcessDetailActions } from './ProcessDetailActions';
import { ProcessDetailContent } from './ProcessDetailContent';
import { Center, Loader, Text, UnstyledButton } from '@mantine/core';
import { useAtomValue } from 'jotai';
import { ChevronLeft } from 'lucide-react';

type ProcessDetailPanelProps = {
  readonly onBack?: () => void;
};

export const ProcessDetailPanel = ({ onBack }: ProcessDetailPanelProps) => {
  const selectedProcessId = useAtomValue(selectedProcessIdAtom);
  const { db } = useDatabase();
  const { isLoading, process } = useProcess(selectedProcessId);

  const { mutateAsync: updateProcess } = trpc.processes.update.useMutation();

  if (!selectedProcessId) {
    return (
      <div className="h-full flex items-center justify-center">
        <Text className="text-sm text-black/30 dark:text-white/30">
          Select a process to view details
        </Text>
      </div>
    );
  }

  if (isLoading || !process) {
    return (
      <Center className="h-full">
        <Loader
          color="gray"
          size="sm"
        />
      </Center>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700">
        {onBack && (
          <UnstyledButton
            className="flex items-center gap-1 text-sm text-blue-500 mb-1"
            onClick={onBack}
          >
            <ChevronLeft size={16} />
            Back to list
          </UnstyledButton>
        )}
        <div className="mb-2">
          <InlineEditable
            defaultValue={process.title}
            onChange={async (newTitle) => {
              const result = await updateProcess({
                processId: process.id,
                title: newTitle,
              });
              if (result.ok) {
                const doc = await db.processes.findOne(process.id).exec();
                await doc?.patch({
                  title: newTitle,
                  updatedAt: new Date().toISOString(),
                });
              }
            }}
            renderDefault={(value) => (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {value}
              </h2>
            )}
            style={{}}
          />
        </div>
        <ProcessDetailActions
          archivedAt={process.archivedAt ? new Date(process.archivedAt) : null}
          isPinned={process.isPinned}
          processId={process.id}
          snoozedUntil={
            process.snoozedUntil ? new Date(process.snoozedUntil) : null
          }
          workspaceId={process.workspaceId}
        />
      </div>

      <ProcessDetailContent processId={process.id} />
    </div>
  );
};
